import { UploadPhotoTemplate } from '@templates';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ErrorMessages, ServiceLocationDetailsObjectType } from '@types';
import * as Sentry from '@sentry/react';
import { FileObject, useDeleteImage, useUploadImage } from '@hooks';
import {
  convertFilePartsToFileObjects,
  handleFileChange,
  handleFileDelete,
  handleUpdateUploadedImages,
  handleUploadFile,
  PartitionFileObjectsType,
} from '@utils';
import {
  updateStoreByKey,
  useEVChargerStore,
} from '../../../../hooks/useEVChargerStore';
import { RoutesList } from '../../../routes/Routes';
import { CustomPhotoUploadTextContent } from './CustomPhotoUploadTextContent';

export const UploadPhotoPage = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState<FileObject[]>([]);
  const {
    update,
    data: {
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
      fileWithLoadedCoordinates,
      serviceLocationDetails,
    },
  } = useEVChargerStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const { mutateAsync: mutateImageUpload, isPending } = useUploadImage();
  const { mutate: mutateDelete } = useDeleteImage();
  const [processingPhoto, setProcessingPhoto] = useState(false);

  const title = 'Upload a photo of the EV charging point';
  const description = <CustomPhotoUploadTextContent />;

  useEffect(() => {
    convertFilePartsToFileObjects({
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
    }).then((images) => {
      setUploadedFiles(images);
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFileChange = async (files: File[]) => {
    setProcessingPhoto(true);
    await handleFileChange({
      files,
      setProcessingPhoto,
      uploadedFiles,
      setUploadedFiles,
      setErrorMessages,
      updateUploadedImages: (images: PartitionFileObjectsType) =>
        handleUpdateUploadedImages(
          images,
          serviceLocationDetails,
          updateStoreByKey,
        ),
      updateIsPhotoLocationStored: (isPhotoLocationStored: boolean) =>
        update('isPhotoLocationStored', isPhotoLocationStored),
      updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) =>
        update('fileWithLoadedCoordinates', fileWithLoadedCoordinates),
      updateServiceLocationDetails: (
        serviceLocationDetails: ServiceLocationDetailsObjectType,
      ) => update('serviceLocationDetails', serviceLocationDetails),
      errorPath: 'upload-photo',
    });
    setProcessingPhoto(false);
  };

  const handleDelete = (files: File[]) => {
    handleFileDelete({
      files,
      setProcessingPhoto,
      uploadedFiles,
      setUploadedFiles,
      setErrorMessages,
      updateUploadedImages: (images: PartitionFileObjectsType) =>
        handleUpdateUploadedImages(
          images,
          serviceLocationDetails,
          updateStoreByKey,
        ),
      updateIsPhotoLocationStored: (isPhotoLocationStored: boolean) =>
        update('isPhotoLocationStored', isPhotoLocationStored),
      fileWithLoadedCoordinates,
      updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) =>
        update('fileWithLoadedCoordinates', fileWithLoadedCoordinates),
      updateServiceLocationDetails: (
        serviceLocationDetails: ServiceLocationDetailsObjectType,
      ) => update('serviceLocationDetails', serviceLocationDetails),
      mutateDelete,
      errorPath: 'upload-photo',
    });
  };

  const handleUploadError = (error: string) => {
    if (error) {
      Sentry.captureException(error);
      setErrorMessages([{ path: 'upload-photo', message: error }]);
    }
  };

  const handleNext = async () => {
    await handleUploadFile({
      uploadedFiles,
      uploadedImagesBlob,
      navigate,
      navigatePath: RoutesList.MAP_LOCATION,
      mutateImageUpload,
      errorPath: 'upload-photo',
      setErrorMessages,
      updateUploadedImagesBlobs: (
        imagesBlobs: {
          blob_uri: string;
          blob_endpoint: string;
          filename: string;
        }[],
      ) => update('uploadedImagesBlob', imagesBlobs),
    });
  };

  const handlePrevious = () => {
    navigate(RoutesList.PROVIDER_SELECTION);
  };

  const buttonNext = {
    label: 'I do not have a photo',
    loading: isPending,
    disabled: processingPhoto,
    onClick: handleNext,
  };

  const buttonPrevious = {
    disabled: processingPhoto || isPending,
    onClick: handlePrevious,
  };

  return (
    <UploadPhotoTemplate
      formTitle={title}
      description={description}
      handleFileChange={onFileChange}
      handleUploadError={handleUploadError}
      handleDelete={handleDelete}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      maxNumberOfUpload={5}
      errorPath={'upload-photo'}
      errorMessages={errorMessages}
      initialFiles={uploadedFiles.map((file) => file.file)}
    />
  );
};
